import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { Box, CircularProgress } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useDrawerTriggerNotification, useRecommendationsDrawerContext } from "../../../../contexts/RecommendationsDrawerContext";
import { useRequestsContext } from "../../../../contexts/RequestsContext";
import { useSidePanelHost } from "../../../../contexts/sidePanel/SidePanelHostContext";
import type { SidePanelHost } from "../../../../contexts/sidePanel/sidePanelTypes";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { NEUTRAL, SURFACE } from "../../../../theme/tokens";
import { formatMonthRangeWithYear } from "../../../../utils/dateFormatting";
import { isNurseActiveInDepartment } from "../../../../utils/formatters";
import { type Nurse, type NurseRole, RecommendationOpenSource } from "../../../../utils/models";
import { countRequestsWithResponses, rolesMap } from "../../../../utils/utilFunctions";
import { DrawerTriggerButton } from "../../../DrawerTriggerButton/DrawerTriggerButton";
import { StaffAutoComplete } from "../../../UIToolkit/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "../../../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { EmptyState } from "../../../UIToolkit/molecules/EmptyState";
import { ScheduleExportButton } from "../../ScheduleExport/ScheduleExportButton";
import { StaffingDateTypeSelector } from "../../StaffingDateTypeSelector";
import { MonthlyDaySidebar } from "./MonthlyDaySidebar";
import { MonthlyFilterBar } from "./MonthlyFilterBar";
import { MonthlyJobGrid } from "./MonthlyJobGrid";
import { MonthlyViewConfigButton } from "./MonthlyViewConfigButton";
import { getMergedDay, type MonthlyDayFilters } from "./monthlyJobUtils";
import { useMonthlyJobViewState } from "./useMonthlyJobViewState";

/**
 * This view is a flex ROW item of `ContentWrapper.module.scss` `.window` (`flex-direction: row;
 * align-items: flex-start`), so without these it sizes to its content and renders as a narrow strip
 * roughly two thirds of the page wide. Mirrors the sibling `.staffContainer` in `Staffing.module.scss`;
 * `alignSelf: stretch` is what defeats the parent's `align-items: flex-start`. `.window` is shared by
 * every page, so the fix belongs here and not there.
 */
const FILL_PARENT_SX = { flex: 1, alignSelf: "stretch", width: "100%", minWidth: 0, height: "100%" } as const;

export const MonthlyJobView: FC = () => {
	const { t } = useTranslation();
	const state = useMonthlyJobViewState();
	const { hospitalData, chosenDepartment, isAutoScheduler, isInitialLoading } = useHospitalData();

	const { isOpen: drawerOpen, toggleDrawerOpen } = useRecommendationsDrawerContext();
	const { getDrawerTriggerNotification } = useDrawerTriggerNotification();
	const { staffRequests } = useRequestsContext();
	const requestsWithResponsesCount = useMemo(() => countRequestsWithResponses(staffRequests), [staffRequests]);
	const notification = getDrawerTriggerNotification(requestsWithResponsesCount);

	const formatPersonName = useFormatPersonName();
	const nurseOptions = useMemo<SystemAutoCompleteOption<Nurse>[]>(
		() =>
			hospitalData.nurses
				.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id))
				.map((nurse) => ({ label: formatPersonName(nurse), value: nurse })),
		[hospitalData.nurses, chosenDepartment.department_id, formatPersonName],
	);

	const availableLocations = useMemo(
		() => [...new Set(state.locations.map((location) => location.location).filter((name): name is string => Boolean(name)))],
		[state.locations],
	);

	// Window / role / job / location filters narrow the aggregated totals. Staff search is deliberately NOT
	// here — it only highlights the searched nurse's days (see `highlightNurseId`), so other staff are never
	// undercounted. The job filter is reconciled against the currently-visible jobs so a selection that's
	// since been hidden via the settings config can't zero out the whole grid (an empty result = "show all").
	const filters = useMemo<MonthlyDayFilters>(() => {
		const visibleIds = new Set(state.visibleJobs.map((job) => job.id));
		const jobFilterIds = new Set(state.jobFilterIds.filter((id) => visibleIds.has(id)));
		// Shifts carry the FULL role name, the selector emits the short one.
		const roleFilter = new Set(state.roleFilter.filter((role): role is NurseRole => role !== "all").map((role) => rolesMap[role] as string));
		// Empty OR fully populated both mean "show all" — `DayViewLocationFilter`'s own semantics.
		const locationFilter = new Set(state.locationFilter.length === availableLocations.length ? [] : state.locationFilter);
		return { jobFilterIds, roleFilter, locationFilter, windowKey: state.windowKey };
	}, [state.jobFilterIds, state.roleFilter, state.locationFilter, state.windowKey, state.visibleJobs, availableLocations.length]);

	// --- Sidebar ---
	// Stable identity so the memoized `MonthlyJobCell` isn't re-rendered by every router-driven URL write.
	const openSidebar = state.openSidebar;
	const handleOpenSidebar = useCallback(
		(weekIndex: number, dayIndex: number, date: DateTime, focusJobId?: string) => openSidebar({ weekIndex, dayIndex, date, focusJobId }),
		[openSidebar],
	);

	// Stable identities so the memoized `MonthlyJobCell` isn't re-rendered by every router-driven URL write.
	const openDayView = state.handleOpenDayView;
	const handleOpenDayViewFromCell = useCallback((date: DateTime) => openDayView(date, "cell"), [openDayView]);
	const handleOpenDayViewFromSidebar = useCallback((date: DateTime) => openDayView(date, "sidebar"), [openDayView]);

	const handleDrawerClose = useCallback(() => {
		toggleDrawerOpen(false, RecommendationOpenSource.WeeklyView);
	}, [toggleDrawerOpen]);
	// The admin side panel docks over the month's right edge; the month has no recommendations, so no such tab.
	const sidePanelHost = useMemo<SidePanelHost>(() => ({ recommendations: null, onClose: handleDrawerClose }), [handleDrawerClose]);
	useSidePanelHost(sidePanelHost);

	const selectedMergedDay = useMemo(
		() => (state.sidebarSelection ? getMergedDay(state.weeks[state.sidebarSelection.weekIndex], state.sidebarSelection.dayIndex) : undefined),
		[state.sidebarSelection, state.weeks],
	);

	// Distinguish loading from "loaded but no schedule". The provider only renders this view once its own
	// initial fetch settles, so an empty `weeks` here is not a loading state — it means the department fetch
	// failed or returned nothing. Showing a spinner in that case would spin forever; show an empty state
	// instead, and reserve the spinner for the genuine in-flight case.
	if (state.weeks.length === 0) {
		return (
			<Box
				data-testid="monthly-job-view-root"
				sx={{ display: "flex", alignItems: "center", justifyContent: "center", ...FILL_PARENT_SX, backgroundColor: SURFACE }}
			>
				{isInitialLoading ? (
					<CircularProgress />
				) : (
					<EmptyState
						icon={<CalendarMonthOutlinedIcon />}
						title={t("monthly.view.empty.title")}
						hint={t("monthly.view.empty.hint")}
						dataTestId="monthly-job-view-empty"
					/>
				)}
			</Box>
		);
	}

	return (
		<Box data-testid="monthly-job-view-root" sx={{ display: "flex", flexDirection: "column", ...FILL_PARENT_SX, backgroundColor: SURFACE, overflow: "hidden" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 1.5,
					px: 2,
					py: 1,
					borderBottom: `1px solid ${NEUTRAL[300]}`,
				}}
			>
				<StaffingDateTypeSelector
					currentDateText={formatMonthRangeWithYear(state.currentTimeFrameStartDate, state.currentTimeFrameEndDate)}
					onResetButtonClick={state.handleToday}
					onNextClick={state.handleNext}
					onPreviousClick={state.handlePrev}
					initialPageType="MONTHLY"
					weekIndex={state.weekIndex}
					setWeekIndex={state.setWeekIndex}
					isPrevDisabled={state.isPrevDisabled}
					isNextDisabled={state.isNextDisabled}
					currentDate={state.currentTimeFrameStartDate}
					onDateSelected={state.handleDatePickerSelected}
					minDate={state.minDate}
					maxDate={state.maxDate}
				/>
				<StaffAutoComplete nurses={nurseOptions} value={state.searchedNurse} onChange={(_, value) => state.setSearchedNurse(value)} width="220px" />
				<Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
					<ScheduleExportButton weekIndex={state.weekIndex} />
					<MonthlyViewConfigButton
						totalsMode={state.totalsMode}
						onTotalsModeChange={state.setTotalsMode}
						enabledJobs={state.enabledJobs}
						visibleJobIds={new Set(state.visibleJobs.map((job) => job.id))}
						onVisibleJobsChange={state.setVisibleJobIds}
					/>
					<DrawerTriggerButton open={drawerOpen} notification={notification} onClick={() => toggleDrawerOpen(true, RecommendationOpenSource.WeeklyView)} />
				</Box>
			</Box>

			<MonthlyFilterBar
				windows={state.shiftWindows}
				windowKey={state.windowKey}
				onWindowKeyChange={state.setWindowKey}
				roleFilter={state.roleFilter}
				onRoleFilterChange={state.setRoleFilter}
				jobFilterIds={state.jobFilterIds}
				onJobFilterChange={state.setJobFilterIds}
				visibleJobs={state.visibleJobs}
				selectedLocations={state.locationFilter}
				setSelectedLocations={state.setLocationFilter}
				availableLocations={availableLocations}
				totalsMode={state.totalsMode}
			/>

			{/* Sizes the grid, but is NOT a scroll owner: `MonthlyJobGrid` owns the only vertical scroll box,
			    so the two nested `overflowY: auto` that used to fight each other are down to one. */}
			<Box sx={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", px: 2, py: 1 }}>
				<MonthlyJobGrid
					visibleWeeks={state.visibleWeeks}
					visibleJobs={state.countedJobs}
					windows={state.shiftWindows}
					totalsMode={state.totalsMode}
					filters={filters}
					today={state.today}
					isPastDay={state.isPastDay}
					isAutoScheduler={isAutoScheduler}
					highlightNurseId={state.searchedNurse?.value.id}
					onOpenSidebar={handleOpenSidebar}
					onOpenDayView={handleOpenDayViewFromCell}
					onWeekNumberClick={state.handleWeekNumberClick}
				/>
			</Box>

			{state.sidebarSelection && (
				// Remount-keyed on the day AND the focused job. The drawer seeds its own job-chip selection
				// from `initialFocusJobId` in `useState`, and it stays mounted while `openSidebar` swaps the
				// selection underneath it — so without this, clicking an Aide pill on one day and then any
				// other day re-opened the drawer still filtered to Aides, with no chip explaining why.
				<MonthlyDaySidebar
					key={`${state.sidebarSelection.date.toFormat("yyyy-MM-dd")}:${state.sidebarSelection.focusJobId ?? "all"}`}
					day={selectedMergedDay}
					date={state.sidebarSelection.date}
					showActual={state.isPastDay(state.sidebarSelection.date)}
					visibleJobs={state.countedJobs}
					windows={state.shiftWindows}
					dayBoundary={state.dayBoundary}
					departmentId={state.departmentId}
					sourceWeekIndex={state.sidebarSelection.weekIndex}
					sourceDayIndex={state.sidebarSelection.dayIndex}
					filters={filters}
					isAutoScheduler={isAutoScheduler}
					initialFocusJobId={state.sidebarSelection.focusJobId}
					onOpenDayView={handleOpenDayViewFromSidebar}
					onClose={state.closeSidebar}
				/>
			)}
		</Box>
	);
};
